"use client";

import { SOCIAL_LINKS } from "@guru/shared/constants/social-links";
import { useTranslation } from "@guru/shared/i18n";
import {
  CreditCard,
  Layers,
  type LucideIcon,
  MessageCircle,
  Percent,
  ShieldCheck,
  Wallet,
} from "lucide-react";
import { FaqList } from "@/components/marketing/faq-list";
import { ScrollReveal } from "@/components/public/scroll-reveal";

/** Decorative icons, aligned by index to the homepage FAQ copy. */
const FAQ_ICONS: LucideIcon[] = [
  CreditCard, // credit card to start
  Wallet, // payment methods
  ShieldCheck, // what if it doesn't work
  Percent, // collection fees
  Layers, // which plan
];

export function FAQSection() {
  const { t } = useTranslation();
  const whatsappLink = SOCIAL_LINKS.find((s) => s.name === "WhatsApp")?.href;

  return (
    <section id="faq" className="relative overflow-clip bg-background py-20 sm:py-24">
      {/* Ambient ledger texture, faded to the top like the other sections */}
      <div
        className="ledger-grid ledger-grid-fade pointer-events-none absolute inset-0"
        aria-hidden="true"
      />

      <div className="relative z-10 mx-auto grid w-full max-w-6xl gap-10 px-4 sm:px-6 lg:grid-cols-[minmax(0,22rem)_1fr] lg:gap-14 lg:px-8">
        {/* Intro + escape hatch — sticky on desktop so it stays with the list */}
        <ScrollReveal threshold={0.3} className="lg:sticky lg:top-28 lg:self-start">
          <p className="mb-3 text-sm font-semibold uppercase tracking-widest text-brand-deep">
            Straight answers
          </p>
          <h2 className="text-balance text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
            {t.homepage.faq.title}
          </h2>
          <p className="mt-4 text-pretty leading-relaxed text-muted-foreground">
            No jargon, no asterisks. Tap any question to see exactly how it works.
          </p>

          {whatsappLink && (
            <a
              href={whatsappLink}
              target="_blank"
              rel="noopener noreferrer"
              className="group mt-6 flex items-center gap-3 rounded-xl border border-border bg-card p-4 transition-all duration-300 hover:-translate-y-0.5 hover:border-positive/50 hover:shadow-md"
            >
              <span className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-positive/10 text-positive ring-1 ring-positive/15 transition-transform duration-300 group-hover:scale-110">
                <MessageCircle className="size-5" aria-hidden="true" />
              </span>
              <span className="text-sm">
                <span className="block font-semibold text-foreground">Still have a question?</span>
                <span className="text-muted-foreground">Chat with us on WhatsApp →</span>
              </span>
            </a>
          )}
        </ScrollReveal>

        {/* The list */}
        <ScrollReveal threshold={0.1}>
          <FaqList
            items={t.homepage.faq.items}
            icons={FAQ_ICONS}
            eventName="faq_expand"
            idPrefix="home-faq"
          />
        </ScrollReveal>
      </div>
    </section>
  );
}
